import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, ViewChild, booleanAttribute, forwardRef, inject, output } from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import EasyMDE from 'easymde';
import { MarkdownRendererService } from '../markdown-shared/markdown-renderer.service';

@Component({
  selector: 'app-easymde-markdown-editor',
  standalone: true,
  templateUrl: './easymde-markdown-editor.component.html',
  styleUrl: './easymde-markdown-editor.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EasymdeMarkdownEditorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => EasymdeMarkdownEditorComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EasymdeMarkdownEditorComponent implements AfterViewInit, OnDestroy, ControlValueAccessor, Validator {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly renderer = inject(MarkdownRendererService);
  private value = '';
  private editor: EasyMDE | undefined;
  private previewTimer: ReturnType<typeof setTimeout> | undefined;
  private layoutRefreshTimer: ReturnType<typeof setTimeout> | undefined;
  private layoutRefreshFrame: number | undefined;
  private onChange: (markdown: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;

  @ViewChild('editorElement', { static: true })
  private readonly editorElement!: ElementRef<HTMLTextAreaElement>;

  protected isFullscreen = false;
  protected isDarkMode = false;
  protected isDisabled = false;
  protected isReadonly = false;
  protected isRequired = false;
  protected editorPlaceholder = '開始輸入 Markdown…';

  readonly markdownChange = output<string>();
  readonly fullscreenChange = output<boolean>();

  @Input()
  set markdown(markdown: string | null | undefined) {
    this.setMarkdown(markdown ?? '', false);
  }

  get markdown(): string {
    return this.value;
  }

  @Input()
  set placeholder(value: string) {
    this.editorPlaceholder = value || '開始輸入 Markdown…';
  }

  @Input({ transform: booleanAttribute })
  set disabled(value: boolean) {
    this.isDisabled = value;
    this.updateEditableState();
  }

  @Input({ transform: booleanAttribute })
  set readonly(value: boolean) {
    this.isReadonly = value;
    this.updateEditableState();
  }

  @Input({ transform: booleanAttribute })
  set required(value: boolean) {
    this.isRequired = value;
    this.onValidatorChange();
  }

  @Input({ transform: booleanAttribute })
  set darkMode(value: boolean) {
    this.isDarkMode = value;
  }

  @Input({ transform: booleanAttribute })
  set fullscreen(value: boolean) {
    this.isFullscreen = value;
  }

  ngAfterViewInit(): void {
    this.editor = new EasyMDE({
      element: this.editorElement.nativeElement,
      initialValue: this.value,
      autofocus: false,
      autosave: { enabled: false, uniqueId: 'jk-util-markdown-editor' },
      lineNumbers: true,
      // Keep one Markdown source line on one editor row; horizontal scrolling
      // is preferable here to a soft-wrapped row changing cursor movement.
      lineWrapping: false,
      spellChecker: false,
      nativeSpellcheck: true,
      sideBySideFullscreen: false,
      syncSideBySidePreviewScroll: true,
      minHeight: '26rem',
      placeholder: this.editorPlaceholder,
      toolbarButtonClassPrefix: 'easymde',
      toolbar: ['bold', 'italic', 'strikethrough', 'heading', '|', 'quote', 'unordered-list', 'ordered-list', 'check-list', '|', 'link', 'image', 'code', 'table', '|', 'preview', 'side-by-side', 'fullscreen', '|', 'guide'],
      previewRender: (markdown: string, preview: HTMLElement) => {
        this.scheduleMermaidRender(preview);
        return this.renderer.render(markdown);
      },
    });

    this.editor.codemirror.on('change', () => this.setMarkdown(this.editor?.value() ?? '', true));
    this.editor.codemirror.on('blur', () => this.markTouched());
    this.editor.codemirror.on('cursorActivity', () => this.resetHorizontalScrollAtLineStart());
    this.updateEditableState();
    EasyMDE.toggleSideBySide(this.editor);
    this.refreshAfterSideBySideLayout();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    if (this.previewTimer) {
      clearTimeout(this.previewTimer);
    }
    if (this.layoutRefreshTimer) {
      clearTimeout(this.layoutRefreshTimer);
    }
    if (this.layoutRefreshFrame !== undefined) {
      cancelAnimationFrame(this.layoutRefreshFrame);
    }
    this.editor?.toTextArea();
  }

  writeValue(markdown: string | null): void {
    this.setMarkdown(markdown ?? '', false);
  }

  registerOnChange(fn: (markdown: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(): ValidationErrors | null {
    return this.isRequired && !this.value.trim() ? { required: true } : null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  protected toggleDarkMode(): void {
    this.isDarkMode = !this.isDarkMode;
    this.cdr.markForCheck();
  }

  protected toggleFullscreen(): void {
    if (!this.editor) {
      return;
    }

    EasyMDE.toggleFullScreen(this.editor);
    this.isFullscreen = this.editor.isFullscreenActive();
    this.fullscreenChange.emit(this.isFullscreen);
    this.cdr.markForCheck();
  }

  protected markTouched(): void {
    this.onTouched();
  }

  private setMarkdown(markdown: string, emit: boolean): void {
    if (markdown === this.value && (!this.editor || this.editor.value() === markdown)) {
      return;
    }

    this.value = markdown;
    if (this.editor && this.editor.value() !== markdown) {
      this.editor.value(markdown);
    }

    if (emit) {
      this.onChange(markdown);
      this.markdownChange.emit(markdown);
      this.onValidatorChange();
    }

    this.cdr.markForCheck();
  }

  private updateEditableState(): void {
    this.editor?.codemirror.setOption('readOnly', this.isDisabled || this.isReadonly);
    this.cdr.markForCheck();
  }

  private resetHorizontalScrollAtLineStart(): void {
    const codeMirror = this.editor?.codemirror;
    if (codeMirror?.getCursor().ch === 0) {
      codeMirror.scrollTo(0, null);
    }
  }

  private refreshAfterSideBySideLayout(): void {
    // EasyMDE adds its side-by-side layout class asynchronously. Refresh only
    // after that layout exists so CodeMirror measures wrapped visual lines correctly.
    this.layoutRefreshTimer = setTimeout(() => {
      this.layoutRefreshTimer = undefined;
      this.layoutRefreshFrame = requestAnimationFrame(() => {
        this.layoutRefreshFrame = undefined;
        this.editor?.codemirror.refresh();
        this.resetHorizontalScrollAtLineStart();
      });
    });
  }

  private scheduleMermaidRender(preview: HTMLElement): void {
    if (this.previewTimer) {
      clearTimeout(this.previewTimer);
    }

    this.previewTimer = setTimeout(() => {
      void this.renderer.renderMermaidBlocks(preview);
    });
  }
}
